import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen, Search, ArrowRight, Check, Plus, Flame, CheckCircle2,
  FileText, ClipboardList, Shield, Sparkles, Target, RotateCcw, Bot,
  Award, TrendingUp, Layers, HelpCircle, X, ChevronRight
} from 'lucide-react';
import { useAcademicSubjects, useMySubject, useSelectSubject, useSubjectProgress, sanitizeSubjectSlug } from '../../hooks/useAcademic';
import { useStudentDashboard } from '../../hooks/useStudent';
import { fetchAnalytics } from '../../api/assessment';
import { useAuth } from '../../context/AuthProvider';
import { LoadingState, ErrorState } from '../../components/states/StateViews';
import { Illustration } from '../../components/common/Illustration';
import type { AcademicSubject } from '../../types';
import './subjectsWorkspace.css';

const LOCAL_STORAGE_SELECTED_KEY = 'webfoxx_my_selected_subjects_slugs';

export function SubjectsWorkspace() {
  const navigate = useNavigate();
  const { status, user } = useAuth();
  const authed = status !== 'guest';

  const { data: subjects = [], isLoading, isError, refetch } = useAcademicSubjects();
  const { data: mySubjectData } = useMySubject(authed);
  const selectMut = useSelectSubject();

  // Local state for user selected subjects list (supports multiple subject selection)
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_SELECTED_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return Array.from(new Set(parsed.map(sanitizeSubjectSlug)));
        }
      }
      return [];
    } catch {
      return [];
    }
  });

  // Sync backend single selection if present and local storage is empty
  useEffect(() => {
    if (mySubjectData?.selectedSubjectSlug && selectedSlugs.length === 0) {
      const updated = [sanitizeSubjectSlug(mySubjectData.selectedSubjectSlug)];
      setSelectedSlugs(updated);
      localStorage.setItem(LOCAL_STORAGE_SELECTED_KEY, JSON.stringify(updated));
    }
  }, [mySubjectData, selectedSlugs.length]);

  const saveSelectedSlugs = (slugs: string[]) => {
    const sanitized = Array.from(new Set(slugs.map(sanitizeSubjectSlug)));
    setSelectedSlugs(sanitized);
    localStorage.setItem(LOCAL_STORAGE_SELECTED_KEY, JSON.stringify(sanitized));
    if (sanitized.length > 0) {
      selectMut.mutate({ slug: sanitized[0], selectedSlugs: sanitized } as any);
    } else {
      selectMut.mutate({ slug: '', selectedSlugs: [] } as any);
    }
  };

  const [showAddModal, setShowAddModal] = useState(false);

  if (isLoading) {
    return (
      <section className="adaptive-page subjects-ws">
        <LoadingState label="Loading subjects catalog…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page subjects-ws">
        <ErrorState message="Could not load subjects catalog." onRetry={() => refetch()} />
      </section>
    );
  }

  // STATE DETERMINATION:
  // State 1: VISITOR (Not signed in)
  if (!authed) {
    return <VisitorSubjectsView subjects={subjects} onRequireAuth={() => navigate('/login')} />;
  }

  // State 2: USER — BEFORE SELECTION (Signed in, 0 subjects selected)
  if (selectedSlugs.length === 0) {
    return (
      <UserBeforeSelectionView
        subjects={subjects}
        onConfirmSelection={(slugs) => saveSelectedSlugs(slugs)}
        onSkipToCatalog={() => saveSelectedSlugs([])}
      />
    );
  }

  // State 3: USER — AFTER SELECTION (Signed in, 1+ subjects selected)
  return (
    <>
      <UserAfterSelectionView
        subjects={subjects}
        selectedSlugs={selectedSlugs}
        userName={user?.name ?? 'Aspirant'}
        onOpenAddModal={() => setShowAddModal(true)}
      />
      {showAddModal && (
        <AddSubjectModal
          allSubjects={subjects}
          currentSlugs={selectedSlugs}
          onClose={() => setShowAddModal(false)}
          onSave={(updated) => {
            saveSelectedSlugs(updated);
            setShowAddModal(false);
          }}
        />
      )}
    </>
  );
}

/* ============================================================================
   STATE 1: VISITOR VIEW (Discovery Catalog)
   ============================================================================ */
function VisitorSubjectsView({
  subjects,
  onRequireAuth,
}: {
  subjects: AcademicSubject[];
  onRequireAuth: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState<'CUET_UG' | 'CUET_PG'>('CUET_UG');
  const [categoryFilter, setCategoryFilter] = useState<'All' | 'Languages' | 'Domain' | 'General'>('All');

  const q = query.trim().toLowerCase();

  const filteredSubjects = useMemo(() => {
    return subjects.filter((s) => {
      const isPg = s.slug.startsWith('pg-') || s.code?.includes('QP') || s.name.includes('(PG)');
      const matchesSection = activeSection === 'CUET_PG' ? isPg : !isPg;

      let matchesCat = true;
      if (categoryFilter === 'Languages') matchesCat = s.code ? ['101', '102', '103', '104', '105', '106', '107', '108'].includes(s.code) : false;
      if (categoryFilter === 'General') matchesCat = s.isCommon || s.code === '501';
      if (categoryFilter === 'Domain') matchesCat = !s.isCommon && !(s.code && ['101', '102', '103', '104', '105', '106', '107', '108'].includes(s.code));

      const matchesQuery =
        !q ||
        s.name.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q) ||
        (s.code ?? '').toLowerCase().includes(q);

      return matchesSection && matchesCat && matchesQuery;
    });
  }, [subjects, activeSection, categoryFilter, q]);

  const ugCount = subjects.filter((s) => !(s.slug.startsWith('pg-') || s.code?.includes('QP') || s.name.includes('(PG)'))).length;
  const pgCount = subjects.filter((s) => s.slug.startsWith('pg-') || s.code?.includes('QP') || s.name.includes('(PG)')).length;

  return (
    <div className="adaptive-page subjects-ws">
      {/* Hero Discovery Header */}
      <div className="sws-header-banner">
        <div className="sws-header-top">
          <div className="sws-title-group">
            <span className="chip-pill chip-pill-primary">SUBJECT DISCOVERY</span>
            <h1>Find your CUET subjects</h1>
            <p>
              Explore official CUET (UG) & CUET (PG) subjects, syllabi, PYQs, and preparation resources to plan your success.
            </p>
          </div>
          <Illustration context="page.home" size="sm" style={{ maxHeight: '120px' }} />
        </div>

        {/* Exam Selector Cards */}
        <div className="sws-exam-row">
          <div
            className={`sws-exam-card ${activeSection === 'CUET_UG' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('CUET_UG')}
          >
            <div className="sws-exam-icon"><BookOpen size={22} /></div>
            <div className="sws-exam-info">
              <h3>CUET (UG)</h3>
              <p>Undergraduate Entrance</p>
              <span className="sws-exam-count">{ugCount || 37} Subjects</span>
            </div>
            <ArrowRight size={18} style={{ opacity: activeSection === 'CUET_UG' ? 1 : 0.4 }} />
          </div>

          <div
            className={`sws-exam-card ${activeSection === 'CUET_PG' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('CUET_PG')}
          >
            <div className="sws-exam-icon"><Layers size={22} /></div>
            <div className="sws-exam-info">
              <h3>CUET (PG)</h3>
              <p>Postgraduate Entrance</p>
              <span className="sws-exam-count">{pgCount || 157} Subjects</span>
            </div>
            <ArrowRight size={18} style={{ opacity: activeSection === 'CUET_PG' ? 1 : 0.4 }} />
          </div>
        </div>
      </div>

      {/* Controls Bar: Search & Filter Chips */}
      <div className="sws-controls-bar">
        <div className="sws-search-box">
          <Search size={18} />
          <input
            placeholder="Search subjects, codes, or topics..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="sws-filter-chips">
          {(['All', 'Languages', 'Domain', 'General'] as const).map((cat) => (
            <button
              key={cat}
              className={`sws-chip ${categoryFilter === cat ? 'is-active' : ''}`}
              onClick={() => setCategoryFilter(cat)}
            >
              {cat === 'Domain' ? 'Domain Subjects' : cat === 'General' ? 'General Test' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="sws-visitor-layout">
        <div className="sws-catalog-grid">
          {filteredSubjects.map((s) => (
            <article key={s.id} className="sws-card-v3">
              <div className="sws-card-v3-head">
                <span className="sws-card-badge">Code {s.code || 'NTA'}</span>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>100% Official</span>
              </div>
              <h3>{s.name}</h3>
              <p>{s.description || 'Complete official NTA syllabus, topic-wise practice, and past paper questions.'}</p>
              <div className="sws-card-stats">
                <span><BookOpen size={14} /> Syllabus</span>
                <span><FileText size={14} /> 50+ PYQs</span>
              </div>
              <div className="sws-card-actions">
                <button
                  type="button"
                  className="secondary-action compact"
                  onClick={() => navigate(`/syllabus/${s.slug}`)}
                >
                  Syllabus
                </button>
                <button
                  type="button"
                  className="primary-action compact"
                  onClick={() => navigate(`/syllabus/${s.slug}`)}
                >
                  Explore <ArrowRight size={14} />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Sidebar Promo Card */}
        <aside>
          <div className="sws-promo-card">
            <Illustration context="card.course" size="xs" style={{ maxHeight: '80px' }} />
            <h3>Build your preparation the right way</h3>
            <p>
              Select your subjects and get access to structured syllabus, practice questions, PYQs, tests, and AI-powered insights.
            </p>
            <ul className="sws-promo-points">
              <li><CheckCircle2 size={16} /> Structured NTA Syllabus</li>
              <li><CheckCircle2 size={16} /> Chapter-wise PYQ Practice</li>
              <li><CheckCircle2 size={16} /> Full Mock Tests & Analytics</li>
            </ul>
            <button
              type="button"
              className="primary-action"
              style={{ width: '100%' }}
              onClick={onRequireAuth}
            >
              Get Started for Free
            </button>
          </div>
        </aside>
      </div>

      {/* Trust Badges Bar */}
      <div className="sws-trust-bar">
        <div className="sws-trust-item">
          <div className="sws-trust-icon"><CheckCircle2 size={20} /></div>
          <div className="sws-trust-info">
            <strong>100% Official</strong>
            <span>Based on NTA guidelines</span>
          </div>
        </div>
        <div className="sws-trust-item">
          <div className="sws-trust-icon"><Shield size={20} /></div>
          <div className="sws-trust-info">
            <strong>Updated Regularly</strong>
            <span>Aligned with latest patterns</span>
          </div>
        </div>
        <div className="sws-trust-item">
          <div className="sws-trust-icon"><Target size={20} /></div>
          <div className="sws-trust-info">
            <strong>Practice Smarter</strong>
            <span>Topic-wise PYQs & Tests</span>
          </div>
        </div>
        <div className="sws-trust-item">
          <div className="sws-trust-icon"><Sparkles size={20} /></div>
          <div className="sws-trust-info">
            <strong>AI-Powered</strong>
            <span>Personalized recommendations</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   STATE 2: USER BEFORE SELECTION VIEW (Guided Onboarding Stepper)
   ============================================================================ */
function UserBeforeSelectionView({
  subjects,
  onConfirmSelection,
  onSkipToCatalog,
}: {
  subjects: AcademicSubject[];
  onConfirmSelection: (slugs: string[]) => void;
  onSkipToCatalog: () => void;
}) {
  const [examType, setExamType] = useState<'UG' | 'PG'>('UG');
  const [search, setSearch] = useState('');
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);

  const filteredList = useMemo(() => {
    const isUg = examType === 'UG';
    const q = search.trim().toLowerCase();

    return subjects.filter((s) => {
      const isPg = s.slug.startsWith('pg-') || s.code?.includes('QP') || s.name.includes('(PG)');
      const matchExam = isUg ? !isPg : isPg;
      const matchSearch = !q || s.name.toLowerCase().includes(q) || (s.code ?? '').toLowerCase().includes(q);
      return matchExam && matchSearch;
    });
  }, [subjects, examType, search]);

  const toggleSlug = (slug: string) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]
    );
  };

  return (
    <div className="adaptive-page subjects-ws">
      {/* Onboarding Header Banner */}
      <div className="sws-header-banner">
        <span className="chip-pill chip-pill-primary">BUILD YOUR PREPARATION</span>
        <div className="sws-header-top">
          <div className="sws-title-group">
            <h1>Let's set up your CUET preparation</h1>
            <p>Choose your exam and select the subjects you want to prepare for.</p>
          </div>
          <Illustration context="onboarding.auth" size="sm" style={{ maxHeight: '110px' }} />
        </div>

        {/* Stepper Header */}
        <div className="sws-stepper">
          <div className="sws-step-item is-active">
            <span className="sws-step-num">1</span>
            <span>Choose Exam</span>
          </div>
          <div className="sws-step-divider" />
          <div className="sws-step-item is-active">
            <span className="sws-step-num">2</span>
            <span>Select Subjects</span>
          </div>
          <div className="sws-step-divider" />
          <div className="sws-step-item">
            <span className="sws-step-num">3</span>
            <span>Confirm & Continue</span>
          </div>
        </div>
      </div>

      {/* Main Selection Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>
        {/* Step 1: Exam Type Radio Cards */}
        <div>
          <h3 style={{ fontSize: '0.95rem', color: '#64748b', marginBottom: '12px' }}>STEP 1: Choose your exam</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div
              className={`sws-exam-card ${examType === 'UG' ? 'is-active' : ''}`}
              onClick={() => setExamType('UG')}
            >
              <div className="sws-exam-icon"><BookOpen size={20} /></div>
              <div className="sws-exam-info">
                <h3>CUET (UG)</h3>
                <p>Undergraduate Entrance</p>
                <span className="sws-exam-count">37 Subjects</span>
              </div>
            </div>

            <div
              className={`sws-exam-card ${examType === 'PG' ? 'is-active' : ''}`}
              onClick={() => setExamType('PG')}
            >
              <div className="sws-exam-icon"><Layers size={20} /></div>
              <div className="sws-exam-info">
                <h3>CUET (PG)</h3>
                <p>Postgraduate Entrance</p>
                <span className="sws-exam-count">157 Subjects</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Subject Checkbox Picker */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '0.95rem', color: '#64748b', margin: 0 }}>STEP 2: Select your subjects</h3>
            <span style={{ fontSize: '0.85rem', color: '#ff6b00', fontWeight: 600 }}>
              {selectedSlugs.length} subject{selectedSlugs.length !== 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="sws-search-box" style={{ marginBottom: '16px' }}>
            <Search size={18} />
            <input
              placeholder="Search subjects, codes, or keywords..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="sws-picker-grid">
            {filteredList.map((s) => {
              const isSelected = selectedSlugs.includes(s.slug);
              return (
                <div
                  key={s.id}
                  className={`sws-picker-card ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => toggleSlug(s.slug)}
                >
                  <div className="sws-picker-info">
                    <h4>{s.name}</h4>
                    <span>Code {s.code || 'NTA'}</span>
                  </div>
                  <div className="sws-checkbox">
                    <Check size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating Selection Action Bar */}
      <div className="sws-selection-bar">
        <div className="sws-selection-info">
          <BookOpen size={20} />
          <span className="sws-selection-count">
            Selected Subjects ({selectedSlugs.length})
          </span>
        </div>
        <button
          type="button"
          className="primary-action"
          disabled={selectedSlugs.length === 0}
          onClick={() => onConfirmSelection(selectedSlugs)}
        >
          Continue to My Subjects <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
   STATE 3: USER AFTER SELECTION VIEW (My Subjects Workspace)
   ============================================================================ */
function UserAfterSelectionView({
  subjects,
  selectedSlugs,
  userName,
  onOpenAddModal,
}: {
  subjects: AcademicSubject[];
  selectedSlugs: string[];
  userName: string;
  onOpenAddModal: () => void;
}) {
  const navigate = useNavigate();

  const { data: analytics } = useQuery({
    queryKey: ['assessment', 'analytics'],
    queryFn: fetchAnalytics,
  });

  const { data: dashboard } = useStudentDashboard(true);

  const selectedList = useMemo(() => {
    return subjects.filter((s) => selectedSlugs.includes(s.slug));
  }, [subjects, selectedSlugs]);

  const avgAccuracy = analytics?.avgAccuracy ?? 0;
  const testsAttempted = analytics?.testsAttempted ?? 0;
  const topicsAnalysed = analytics?.topicsAnalysed ?? 0;
  const streakDays = dashboard?.streak ?? 1;

  return (
    <div className="adaptive-page subjects-ws">
      {/* Header Banner */}
      <div className="sws-header-banner">
        <div className="sws-header-top">
          <div className="sws-title-group">
            <span className="chip-pill chip-pill-primary">WELCOME BACK, {userName.toUpperCase()} 👋</span>
            <h1>My Subjects</h1>
            <p>
              Continue your CUET preparation. Track progress, practice PYQs, and improve every day.
            </p>
          </div>
          <button type="button" className="primary-action compact" onClick={onOpenAddModal}>
            <Plus size={16} /> Add Subject
          </button>
        </div>
      </div>

      {/* Stats Tile Row */}
      <div className="sws-stats-row">
        <div className="sws-stat-tile">
          <div className="sws-stat-icon tone-orange"><BookOpen size={22} /></div>
          <div>
            <div className="sws-stat-value">{selectedList.length}</div>
            <div className="sws-stat-label">Subjects Selected</div>
          </div>
        </div>

        <div className="sws-stat-tile">
          <div className="sws-stat-icon tone-green"><TrendingUp size={22} /></div>
          <div>
            <div className="sws-stat-value">{avgAccuracy}%</div>
            <div className="sws-stat-label">Average Accuracy</div>
          </div>
        </div>

        <div className="sws-stat-tile">
          <div className="sws-stat-icon tone-purple"><FileText size={22} /></div>
          <div>
            <div className="sws-stat-value">{topicsAnalysed}</div>
            <div className="sws-stat-label">Topics Analysed</div>
          </div>
        </div>

        <div className="sws-stat-tile">
          <div className="sws-stat-icon tone-blue"><ClipboardList size={22} /></div>
          <div>
            <div className="sws-stat-value">{testsAttempted}</div>
            <div className="sws-stat-label">Tests Attempted</div>
          </div>
        </div>
      </div>

      {/* Workspace Main Grid */}
      <div className="sws-my-layout">
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Your Subjects ({selectedList.length})
            </h2>
            <button type="button" className="text-action" onClick={onOpenAddModal}>
              <Plus size={15} /> Add Subject
            </button>
          </div>

          {/* Selected Subject Cards */}
          {selectedList.map((s) => (
            <SelectedSubjectRowCard
              key={s.id}
              subject={s}
              onNavigate={() => navigate(`/syllabus/${s.slug}`)}
            />
          ))}
        </div>

        {/* Right Sidebar Widgets */}
        <aside>
          {/* Study Streak Widget */}
          <div className="sws-sidebar-box">
            <h4><Flame size={18} style={{ color: '#ff6b00' }} /> Study Streak</h4>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>{streakDays} Day{streakDays !== 1 ? 's' : ''}</div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '2px 0 12px 0' }}>Keep it up! Consistency is key.</p>
            <div className="sws-streak-days">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                <div key={i} className={`sws-streak-day ${i < streakDays ? 'is-done' : ''}`}>
                  <span>{day}</span>
                  <div className="sws-streak-dot">{i < streakDays && <Check size={12} />}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions Widget */}
          <div className="sws-sidebar-box">
            <h4>Quick Actions</h4>
            <div className="sws-quick-actions">
              <div className="sws-action-item" onClick={() => navigate('/assessment')}>
                <span>Take a Mock Test</span> <ChevronRight size={15} />
              </div>
              <div className="sws-action-item" onClick={() => navigate('/assessment')}>
                <span>Practice PYQs</span> <ChevronRight size={15} />
              </div>
              <div className="sws-action-item" onClick={() => navigate('/analytics')}>
                <span>Weak Areas</span> <ChevronRight size={15} />
              </div>
              <div className="sws-action-item" onClick={() => navigate('/ai')}>
                <span>AI Doubt Solver</span> <ChevronRight size={15} />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SelectedSubjectRowCard({
  subject,
  onNavigate,
}: {
  subject: AcademicSubject;
  onNavigate: () => void;
}) {
  const { data: progress } = useSubjectProgress(subject.slug, true);

  const isPg = subject.slug.startsWith('pg-') || subject.code?.includes('QP') || subject.name.includes('(PG)');
  const isGt = subject.isCommon || subject.code === '501';
  const initials = subject.name.substring(0, 2).toUpperCase();

  const totalTopics = progress?.total ?? 18;
  const completedTopics = progress?.completed ?? 0;
  const percent = progress?.percent ?? 0;

  return (
    <div className="sws-my-card">
      <div className={`sws-avatar-badge ${isGt ? 'is-gt' : isPg ? 'is-pg' : ''}`}>
        {initials}
      </div>

      <div className="sws-my-info">
        <h3>{subject.name}</h3>
        <div className="sws-my-meta">
          <span>{isPg ? 'CUET (PG)' : 'CUET (UG)'} • {subject.code || '501'}</span>
          <span className="sws-tag-pill">{isGt ? 'General Test' : isPg ? 'Domain Subject' : 'Language'}</span>
        </div>
        <div className="sws-my-metrics">
          <span><BookOpen size={13} /> {completedTopics}/{totalTopics} Topics</span>
          <span><FileText size={13} /> Official PYQs</span>
          <span><ClipboardList size={13} /> Tests Ready</span>
        </div>
      </div>

      <div className="sws-progress-block">
        <div className="sws-progress-head">
          <span>Progress</span>
          <span>{percent}%</span>
        </div>
        <div className="sws-progress-track">
          <div className="sws-progress-fill" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <div>
        <button
          type="button"
          className="primary-action compact"
          style={{ width: '100%' }}
          onClick={onNavigate}
        >
          {percent > 0 ? 'Continue Learning' : 'Start Learning'} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
   MODAL OVERLAY: ADD SUBJECT PICKER
   ============================================================================ */
function AddSubjectModal({
  allSubjects,
  currentSlugs,
  onClose,
  onSave,
}: {
  allSubjects: AcademicSubject[];
  currentSlugs: string[];
  onClose: () => void;
  onSave: (updated: string[]) => void;
}) {
  const sanitizedCurrent = useMemo(
    () => Array.from(new Set(currentSlugs.map(sanitizeSubjectSlug))),
    [currentSlugs]
  );
  const [selected, setSelected] = useState<string[]>(sanitizedCurrent);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allSubjects.filter(
      (s) => !q || s.name.toLowerCase().includes(q) || (s.code ?? '').toLowerCase().includes(q)
    );
  }, [allSubjects, query]);

  const toggleSlug = (slug: string) => {
    const realSlug = sanitizeSubjectSlug(slug);
    setSelected((prev) => (prev.includes(realSlug) ? prev.filter((x) => x !== realSlug) : [...prev, realSlug]));
  };

  return (
    <div className="sws-modal-backdrop" onClick={onClose}>
      <div className="sws-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sws-modal-header">
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Add Subjects to My Workspace</h3>
          <button type="button" className="ghost-icon-button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="sws-modal-body">
          <div className="sws-search-box" style={{ marginBottom: '16px' }}>
            <Search size={18} />
            <input
              placeholder="Search subjects to add..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="sws-picker-grid">
            {filtered.map((s) => {
              const targetSlug = sanitizeSubjectSlug(s.slug);
              const isSel = selected.includes(targetSlug) || (s.code === '501' && selected.includes('paper-1'));
              return (
                <div
                  key={s.id}
                  className={`sws-picker-card ${isSel ? 'is-selected' : ''}`}
                  onClick={() => toggleSlug(s.slug)}
                >
                  <div className="sws-picker-info">
                    <h4>{s.name}</h4>
                    <span>Code {s.code || 'NTA'}</span>
                  </div>
                  <div className="sws-checkbox">
                    <Check size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="sws-modal-footer">
          <button type="button" className="secondary-action compact" onClick={onClose}>Cancel</button>
          <button type="button" className="primary-action compact" onClick={() => onSave(selected)}>
            Save Subjects ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );
}
